import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

interface LessonContentProps {
  content: string;
  className?: string;
}

const LessonContent: React.FC<LessonContentProps> = ({ content, className }) => {
  return (
    <div className={className}>
      <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        h1: ({ children }) => <h1 className="text-3xl font-bold mb-6 mt-2">{children}</h1>,
        h2: ({ children }) => (
          <h2 className="text-xl font-semibold mb-3 mt-8 border-l-4 border-primary pl-4">
            {children}
          </h2>
        ),
        h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 mt-6">{children}</h3>,
        p: ({ children }) => <p className="text-base leading-relaxed mb-4 text-foreground/90">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
        em: ({ children }) => <em className="italic text-foreground/80">{children}</em>,
        ul: ({ children }) => <ul className="list-disc pl-6 space-y-1 mb-4">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-6 space-y-1 mb-4">{children}</ol>,
        li: ({ children }) => <li className="text-base leading-relaxed">{children}</li>,
        code: ({ inline, className: codeClass, children }) =>
          inline ? (
            <code className="bg-muted text-primary font-mono text-sm px-1.5 py-0.5 rounded">
              {children}
            </code>
          ) : (
            <pre className="bg-muted border border-border rounded-lg p-4 mb-4 overflow-x-auto">
              <code className="font-mono text-sm">{children}</code>
            </pre>
          ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-primary/40 pl-4 italic text-foreground/70 mb-4">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="border-t border-border my-8" />,
        table: ({ children }) => (
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-left">{children}</table>
          </div>
        ),
      }}
    >
      {content}
      </ReactMarkdown>
    </div>
  );
};

export default LessonContent;
